// Client-side WebSocket diagnostics ring buffer.
//
// Lost-session episodes (Cloud Run 1006 drops, the liveness watchdog firing,
// auto-reconnect, a turn-end event that lands during the gap and leaves the
// Send button stale-disabled) are hard to debug from screenshots alone: by the
// time anyone looks, the console has scrolled and the socket has been replaced.
//
// This records every notable WS lifecycle moment into a capped ring buffer on
// `window.__loomWsDiag` AND mirrors it to `console.info` with a `[ws-diag]`
// tag. The ring buffer is what makes it readable after the fact — a Playwright
// driver can dump the full timeline with:
//
//   npx @playwright/cli eval "JSON.stringify(window.__loomWsDiag, null, 2)"
//
// Keep entries cheap and structured; this is a temporary diagnostic surface,
// not a product feature.

export interface WsDiagEntry {
  /** Wall-clock ISO timestamp (browser `Date` — fine in app code). */
  readonly t: string;
  /** Milliseconds since the buffer was first written, for gap analysis. */
  readonly dt: number;
  readonly kind: string;
  readonly detail?: Record<string, unknown>;
}

const MAX_ENTRIES = 400;

interface DiagHost {
  __loomWsDiag?: WsDiagEntry[];
  __loomWsDiagStart?: number;
}

const host = (): DiagHost | null =>
  typeof window === "undefined" ? null : (window as unknown as DiagHost);

export const wsDiag = (
  kind: string,
  detail?: Record<string, unknown>,
  /** High-frequency entries (heartbeats) go to the ring buffer only, never
   *  the console, to keep the console readable. */
  quiet = false,
): void => {
  const h = host();
  const now = Date.now();
  if (h !== null) {
    if (!Array.isArray(h.__loomWsDiag)) {
      h.__loomWsDiag = [];
      h.__loomWsDiagStart = now;
    }
    const start = h.__loomWsDiagStart ?? now;
    const buf = h.__loomWsDiag;
    buf.push({
      t: new Date(now).toISOString(),
      dt: now - start,
      kind,
      ...(detail !== undefined ? { detail } : {}),
    });
    if (buf.length > MAX_ENTRIES) buf.splice(0, buf.length - MAX_ENTRIES);
  }
  if (!quiet) {
    console.info(`[ws-diag] ${kind}`, detail ?? "");
  }
};

/** Convenience for a driver/console: the whole timeline as one string. */
export const dumpWsDiag = (): string => {
  const h = host();
  return JSON.stringify(h?.__loomWsDiag ?? [], null, 2);
};
